import { useReducer, useEffect, useContext } from 'react'
import {
    Typography,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    IconButton,
} from '@mui/material'
import { Delete as DeleteIcon, Person as PersonIcon } from '@mui/icons-material'
import {
    Autocomplete as UniversalAutoComplete,
    Grid,
} from '../../UniversalComponents'

import { getUsersByManufacturer } from '../../../apiRoutes'
import { ToastMessage } from '../../../context/ToastPopUpContext'

interface User {
    _id: string
    userEmail?: string
    userFirstName?: string
    userLastName?: string
}

interface Props {
    manufacturerId: string
    manufacturerName?: string
    manufacturerAdmins: any[]
    onAdminsChange: (admins: User[]) => void
    permissionCheck?: boolean
}

const ManufacturerAdmins = ({
    manufacturerId,
    manufacturerName,
    manufacturerAdmins,
    onAdminsChange,
    permissionCheck,
}: Props) => {
    const setToastMessage = useContext(ToastMessage)

    const defaultState = {
        manufacturerAssignedUsers: [] as User[],
        findUser: '' as any,
        autocompleteResetKey: 0,
    }

    const reducer = (state: any, newState: any) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)

    const { manufacturerAssignedUsers, findUser, autocompleteResetKey } = state

    const getUsersData = async () => {
        try {
            const data = await getUsersByManufacturer({
                active: true,
                manufacturer: manufacturerId,
            })
            if (data && data.length !== 0) {
                setState({ manufacturerAssignedUsers: data })
            } else {
                setState({ manufacturerAssignedUsers: [] })
            }
        } catch (err: any) {
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    useEffect(() => {
        if (manufacturerId) getUsersData()
    }, [manufacturerId])

    const adminObjects: User[] = (manufacturerAdmins || []).map(
        (entry: any) => {
            if (entry && typeof entry === 'object' && entry._id) {
                if (!entry.userEmail) {
                    const hydrated = manufacturerAssignedUsers.find(
                        (u: User) => u._id === entry._id
                    )
                    if (hydrated) return hydrated
                }
                return entry as User
            }
            const id = typeof entry === 'string' ? entry : entry?._id
            return (
                manufacturerAssignedUsers.find((u: User) => u._id === id) || {
                    _id: id,
                    userEmail: '',
                }
            )
        }
    )

    const handleUserChange = (e: any) => {
        const { value } = e.target

        if (!value || !value._id) {
            return
        }

        const user: User = {
            _id: value._id,
            userEmail: value?.userEmail,
            userFirstName: value?.userFirstName,
            userLastName: value?.userLastName,
        }

        const doesUserExist = adminObjects?.some((a) => a._id === user._id)

        if (!doesUserExist) {
            onAdminsChange([...adminObjects, user])
            setState({
                findUser: '',
                autocompleteResetKey: autocompleteResetKey + 1,
            })
        }
    }

    const onUserDelete = (userId: string) => {
        onAdminsChange(adminObjects.filter((a) => a._id !== userId))
    }

    return (
        <Grid item xs={12} md={12}>
            <Grid item xs={12} md={12}>
                <Typography
                    sx={{ marginTop: 5, marginBottom: 5 }}
                    align="center"
                    variant="body1"
                    color="primary"
                >
                    Select users from{' '}
                    <strong>{manufacturerName || 'this manufacturer'}</strong>{' '}
                    to assign as Manufacturer Administrators
                </Typography>
            </Grid>

            <Grid item xs={12} md={12}>
                <UniversalAutoComplete
                    key={autocompleteResetKey}
                    filterOptions={(options: any[], state: any) => {
                        if (!options) return []

                        const inputValue = (state?.inputValue || '')
                            .toLowerCase()
                            .trim()

                        return options.filter((option: any) => {
                            if (
                                adminObjects?.some(
                                    (selected: any) =>
                                        selected?._id === option?._id
                                )
                            ) {
                                return false
                            }

                            if (!inputValue) return true

                            const email = (option.userEmail || '').toLowerCase()
                            const firstName = (
                                option.userFirstName || ''
                            ).toLowerCase()
                            const lastName = (
                                option.userLastName || ''
                            ).toLowerCase()
                            const fullName = `${firstName} ${lastName}`.trim()

                            return (
                                email.includes(inputValue) ||
                                firstName.includes(inputValue) ||
                                lastName.includes(inputValue) ||
                                fullName.includes(inputValue)
                            )
                        })
                    }}
                    getOptionLabel={(option: any) => {
                        if (!option || typeof option !== 'object') {
                            return ''
                        }

                        const firstName = option.userFirstName || ''
                        const lastName = option.userLastName || ''
                        const email = option.userEmail || ''

                        const fullName = [firstName, lastName]
                            .filter(Boolean)
                            .join(' ')

                        return fullName ? `${fullName} (${email})` : email
                    }}
                    options={manufacturerAssignedUsers || []}
                    isOptionEqualToValue={(option: any, value: any) =>
                        option._id === value._id
                    }
                    handleChange={handleUserChange}
                    value={findUser}
                    name="manufacturerAdmins"
                    fullWidth
                    placeholder="Enter an email or name to search..."
                    noOptionsText={'No Remaining Users Found'}
                    label={'Manufacturer Users'}
                    disabled={permissionCheck}
                />
            </Grid>

            <Grid item xs={12} md={12}>
                <List sx={{ mt: 2 }}>
                    {adminObjects?.map((user: User) => (
                        <ListItem
                            key={user._id}
                            secondaryAction={
                                <IconButton
                                    edge="end"
                                    title="Remove Admin"
                                    onClick={() => onUserDelete(user._id)}
                                    disabled={permissionCheck}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            }
                        >
                            <ListItemAvatar>
                                <Avatar>
                                    <PersonIcon />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={user.userEmail}
                                secondary={
                                    user.userFirstName || user.userLastName
                                        ? `${user.userFirstName || ''} ${
                                              user.userLastName || ''
                                          }`.trim()
                                        : undefined
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </Grid>
        </Grid>
    )
}

export default ManufacturerAdmins
